import React, { useEffect, useState } from "react"
import { FormInstance } from "antd"
import { AxiosError } from "axios"

import { APIError, APIResponse, catchErrors } from "@src/shared/api"
import css from "./base_error.module.css"

export const BaseError: React.FC<{
  error?: string
}> = ({ error }) => {
  return error ? <div className={css["base-error"]}>{error}</div> : null
}

interface FormSubmitHook<T> {
  baseError?: string
  submit: (values: T) => Promise<any>
}
export function useCatchFormErrors<F>(
  form: FormInstance<F>,
  onSubmit: (identity: F) => Promise<any>,
): FormSubmitHook<F> {
  const [baseError, setBaseError] = useState<string | undefined>()

  const [formErrors, setFormErrors] = useState(() =>
    form
      .getFieldsError()
      .map((error) => ({ ...error, errors: [] as string[] })),
  )

  useEffect(() => {
    form.setFields(formErrors)
  }, [formErrors])

  const recursionChecks = (error: APIError, name: unknown[] = []) => {
    const target = error.target ? [...name, String(error.target)] : name

    if (error.errors) {
      error.errors.forEach((error: APIError) => recursionChecks(error, target))
    } else {
      setFormErrors((values) => {
        return values.map((f) => {
          const name = Array.isArray(f.name)
            ? f.name.map((i) => String(i))
            : String(f.name)

          if (JSON.stringify(name) === JSON.stringify(target)) {
            return {
              ...f,
              errors: [error.message],
            }
          }

          return f
        })
      })
    }
  }

  return {
    baseError,
    submit: (values) => {
      let newBaseError: string | undefined

      return onSubmit(values)
        .then(() =>
          setFormErrors(
            form
              .getFieldsError()
              .map((error) => ({ ...error, errors: [] as string[] })),
          ),
        )
        .catch((error: AxiosError<APIResponse>) => {
          setFormErrors(
            form
              .getFieldsError()
              .map((fError) => ({ ...fError, errors: [] as string[] })),
          )

          if (error.isAxiosError && error.response) {
            if (error.response.status == 422) {
              let data = error.response.data as APIResponse

              if (data.error) {
                newBaseError = data.error.message

                return recursionChecks(data.error)
              }

              return
            }
          }

          return catchErrors(error)
        })
        .finally(() => {
          setBaseError(newBaseError)

          form.setFields(formErrors)
        })
    },
  }
}
